import React, { useState } from 'react';
import type { LoyaltyReward } from '../types';
import PlusIcon from '../components/icons/PlusIcon';
import EditIcon from '../components/icons/EditIcon';
import TrashIcon from '../components/icons/TrashIcon';
import RewardModal from '../components/loyalty/RewardModal';

interface LoyaltyPageProps {
  rewards: LoyaltyReward[];
  onSave: (reward: LoyaltyReward) => void;
  onDelete: (rewardId: number) => void;
}

const LoyaltyPage: React.FC<LoyaltyPageProps> = ({ rewards, onSave, onDelete }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingReward, setEditingReward] = useState<LoyaltyReward | null>(null);

  const handleOpenModal = (reward: LoyaltyReward | null = null) => {
    setEditingReward(reward);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingReward(null);
  };

  const handleSave = (reward: LoyaltyReward) => {
    onSave(reward);
    handleCloseModal();
  };

  const handleDelete = (rewardId: number) => {
    if (window.confirm('Are you sure you want to delete this reward?')) {
      onDelete(rewardId);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <header className="bg-white shadow-md z-10 flex-shrink-0">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <h1 className="text-2xl font-bold text-gray-800">Customer Loyalty Program</h1>
            <button
              onClick={() => handleOpenModal()}
              className="bg-pink-500 text-white font-bold py-2 px-4 rounded-lg hover:bg-pink-600 transition-colors duration-300 flex items-center"
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              Add New Reward
            </button>
          </div>
        </div>
      </header>
      <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
        <div className="bg-white shadow rounded-lg overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reward Name</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Points Required</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Discount Value</th>
                <th scope="col" className="relative px-6 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {rewards.map((reward) => (
                <tr key={reward.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{reward.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-bold text-gray-900">{reward.pointsRequired}</div>
                  </td>
                   <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-bold text-green-600">${reward.discountValue.toFixed(2)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button onClick={() => handleOpenModal(reward)} className="text-pink-600 hover:text-pink-900 mr-4"><EditIcon className="w-5 h-5" /></button>
                    <button onClick={() => handleDelete(reward.id)} className="text-red-600 hover:text-red-900"><TrashIcon className="w-5 h-5" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
      {isModalOpen && (
        <RewardModal
          reward={editingReward}
          onClose={handleCloseModal}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

export default LoyaltyPage;